import { useGetUserRequest } from '@dealstack/frontend/shared/other/react/hooks/useGetUserRequest';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { UserAppDispatch } from '@dealstack/frontend/shared/other/react/types';
import { useShowErrorToastAndLogout } from '@dealstack/frontend/shared/other/react/hooks/useShowErrorToastAndLogout';
import { useGlobalConfig } from '@dealstack/frontend/shared/globalConfig';
import { MainDesktopLayout } from '@dealstack/frontend/shared/layout/MainLayout/MainDesktopLayout';
import {
	ApplicationType,
	DataUserResponse,
	PermissionRoleWithOwnerEnum,
} from '@dealstack/shared/lib/generated';
import { redirectToLoginPage, returnTargetOf } from '@dealstack/frontend/shared/redirecting';
import { consumeReturnPath } from '@dealstack/frontend/shared/pages/AuthStart/returnPath';
import {
	INVESTOR_PHONE_ROUTE,
	needsInvestorPhone,
} from '@dealstack/frontend/shared/pages/InvestorPhone/needsInvestorPhone';
import { FGAInternalRole } from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';

interface Props {
	children: React.ReactNode;
	defaultGuestRoute: string;
	dispatch: UserAppDispatch;
	applicationType: ApplicationType;
	defaultProtectedRoute: string;
	isInvestorApp?: boolean;
	investmentsDropdown?: React.ReactNode;
	hideOverflowX?: boolean;
}

export const ProtectedRoute = ({
	defaultGuestRoute,
	children,
	dispatch,
	applicationType,
	defaultProtectedRoute,
	isInvestorApp,
	investmentsDropdown,
	hideOverflowX = true,
}: Props) => {
	const location = useLocation();
	const navigate = useNavigate();
	const getUserRequest = useGetUserRequest(dispatch, returnTargetOf(location));

	const globalConfig = useGlobalConfig();

	const [isLoading, setIsLoading] = useState(true);

	const showErrorToastAndLogout = useShowErrorToastAndLogout();

	const hasNoPlatformAccess = (user: DataUserResponse): boolean => {
		return user.platformAccess.length === 0;
	};

	const hasPlatformAccess = (user: DataUserResponse): boolean => {
		return user.platformAccess.some(
			(platform: ApplicationType) => platform === applicationType
		);
	};

	const settleAuthorized = () => {
		const returnPath = consumeReturnPath();
		if (returnPath) {
			navigate(returnPath);
		}
		setIsLoading(false);
	};

	const settleInvestor = (user: DataUserResponse) => {
		if (!needsInvestorPhone(user, applicationType)) {
			settleAuthorized();

			return;
		}

		navigate(INVESTOR_PHONE_ROUTE);
	};

	const checkIfSignedIn = async () => {
		const userResponse = (await getUserRequest.refetch()).data?.payload;

		if (!userResponse) {
			redirectToLoginPage(returnTargetOf(location));
			return;
		}
		const isDealstackSupport = userResponse.permissions?.some(
			(perm) =>
				perm.permissionRole ===
				(FGAInternalRole.DEALSTACK_SUPPORT as unknown as PermissionRoleWithOwnerEnum)
		);

		if (applicationType === ApplicationType.GODMODE) {
			if (!isDealstackSupport) {
				await showErrorToastAndLogout();
				navigate(defaultGuestRoute);
			} else {
				settleAuthorized();
			}
			return;
		}

		if (isDealstackSupport) {
			settleAuthorized();
			return;
		}

		const isAuthorizedForPlatform = hasPlatformAccess(userResponse);
		const lacksPlatformAccess = hasNoPlatformAccess(userResponse);

		if (lacksPlatformAccess) {
			await showErrorToastAndLogout();
			navigate(defaultGuestRoute);

			return;
		}

		if (isAuthorizedForPlatform) {
			settleInvestor(userResponse);
			return;
		}
		// User has access to the other platform
		const swappedApplicationUrlMap = {
			[ApplicationType.DEALSTACK]: globalConfig.investorUrl,
			[ApplicationType.INVESTOR]: globalConfig.appUrl,
		};
		window.location.href = `${swappedApplicationUrlMap[applicationType]}${defaultProtectedRoute}`;
		navigate(defaultGuestRoute);
	};

	useEffect(() => {
		void checkIfSignedIn();
	}, []);

	return (
		<>
			{isLoading ? (
				<FullscreenLoader name="protectedRoute" />
			) : (
				<MainDesktopLayout
					defaultProtectedRoute={defaultProtectedRoute}
					isInvestorApp={isInvestorApp}
					investmentsDropdown={investmentsDropdown}
					hideOverflowX={hideOverflowX}
				>
					{children}
				</MainDesktopLayout>
			)}
		</>
	);
};
